import type { PreviewData } from "./use-theme-config";
import type { translations } from '@/translations/translations';

type T = typeof translations.fr.admin.themeConfig;

interface Props {
  isOpen: boolean;
  previewData: PreviewData | null;
  isExcelLoading: boolean;
  t: T;
  onClose: () => void;
  onSubmit: () => void;
}

export function ExcelPreviewDialog({ isOpen, previewData, isExcelLoading, t, onClose, onSubmit }: Props) {
  if (!isOpen || !previewData) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={onClose}>
      <div className="w-full max-w-4xl max-h-[80vh] overflow-auto rounded-2xl border border-border/40 bg-card p-6" onClick={(e) => e.stopPropagation()}>
        <div className="mb-6">
          <p className="flex items-center gap-3 text-[0.66rem] font-semibold uppercase tracking-[0.25em] text-brand">
            {t.excelForm.previewTitle.replace("{count}", String(previewData.rows.length))}
          </p>
          <p className="mt-3 text-sm leading-6 text-muted-foreground/[0.72]">
            {t.excelForm.readyToImport.replace("{count}", String(previewData.rows.length))}
          </p>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border/40">
                <th className="px-4 py-3 text-left text-[0.62rem] font-semibold uppercase tracking-[0.18em] text-muted-foreground">#</th>
                <th className="px-4 py-3 text-left text-[0.62rem] font-semibold uppercase tracking-[0.18em] text-muted-foreground">FR</th>
              </tr>
            </thead>
            <tbody>
              {previewData.rows.map((row, idx) => (
                <tr key={idx} className="border-b border-border/40 last:border-b-0">
                  <td className="px-4 py-2.5 text-sm text-muted-foreground">{idx + 1}</td>
                  <td className="max-w-xs truncate px-4 py-2.5 text-sm text-foreground">{row.questionFr}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="mt-6 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="inline-flex items-center gap-1.5 rounded-full border border-border/40 bg-transparent px-4 py-2 text-xs font-semibold text-muted-foreground hover:text-foreground">
            {t.excelForm.changeFile}
          </button>
          <button
            type="button"
            onClick={() => { onClose(); onSubmit(); }}
            disabled={isExcelLoading}
            className="inline-flex items-center gap-1.5 rounded-full border border-brand/20 bg-brand px-5 py-2 text-sm font-semibold text-slate-950 transition-colors hover:bg-brand disabled:border-border/40 disabled:bg-muted/20 disabled:text-muted-foreground"
          >
            {isExcelLoading && <span className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-slate-950 border-t-transparent" />}
            {t.excelForm.creatingInProgress}
          </button>
        </div>
      </div>
    </div>
  );
}
